import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

export const LayoutRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  overflow: 'hidden',
  // Allow scrolling in landscape mode
  '@media (max-width: 900px) and (orientation: landscape)': {
    overflow: 'auto',
  },
}));

export const SidebarWrapper = styled(Box)(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  backgroundColor: '#1e2a38',
  [theme.breakpoints.down('md')]: {
    display: 'none', // Hide sidebar on mobile
  },
}));

export const MainWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
  // Allow overflow in landscape mode
  '@media (max-width: 900px) and (orientation: landscape)': {
    overflow: 'auto',
    height: 'auto',
  },
}));

export const HeaderWrapper = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1100,
  backgroundColor: '#fff',
  [theme.breakpoints.down('md')]: {
    position: 'fixed', // Use fixed positioning on mobile to prevent scroll issues
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: 1200, // Higher z-index on mobile
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', // Add shadow for better visibility
  },
  // Hide header in landscape mode on mobile
  '@media (max-width: 900px) and (orientation: landscape)': {
    display: 'none',
  },
}));

export const ContentWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(3),
  overflow: 'auto',
  height: 'calc(100vh - 108px)', // Viewport height minus header (64px) and banner (44px)
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    paddingTop: 'calc(64px + 44px + 24px)', // Account for fixed header (64px) + banner (44px) + extra spacing (24px)
    paddingBottom: theme.spacing(11), // Adjusted bottom padding for 72px mobile bottom navigation
    height: '100vh', // Full viewport height since header is fixed
    minHeight: '100vh', // Ensure minimum height
    maxHeight: '100vh', // Prevent overflow
    scrollBehavior: 'smooth', // Smooth scrolling
    position: 'relative', // Ensure proper positioning
    boxSizing: 'border-box', // Include padding in height calculation
    zIndex: 0, // Ensure content is well below navigation
    isolation: 'auto', // Don't create new stacking context
    // Landscape mode adjustments - no header or bottom nav
    '@media (max-width: 900px) and (orientation: landscape)': {
      paddingTop: '20px !important', // Reduced top padding for landscape mode
      paddingBottom: `${theme.spacing(2)} !important`, // No bottom navigation padding needed
      marginTop: '0px !important', // Remove any top margin
      height: '100vh !important', // Use full viewport height
      maxHeight: '100vh !important', // Set max height to viewport
      minHeight: '100vh !important', // Set min height to viewport
      overflow: 'auto !important', // Enable scrolling
    },
    // iOS Safari PWA specific fixes
    WebkitOverflowScrolling: 'touch', // Enable momentum scrolling on iOS
    '&::-webkit-scrollbar': {
      display: 'none', // Hide scrollbar on mobile
    },
    msOverflowStyle: 'none', // Hide scrollbar on IE/Edge
    scrollbarWidth: 'none', // Hide scrollbar on Firefox
    // Additional iOS PWA fixes
    '@supports (-webkit-touch-callout: none)': {
      // iOS Safari specific styles
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 64px + 44px + 24px)', // Account for safe area
      paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px) + 60px)', // Extra bottom padding for iOS: 88px base + safe area + 60px buffer
      marginTop: '24px', // Add top margin to push content down and fix scroll position issue
      minHeight: 'calc(100vh - env(safe-area-inset-bottom, 0px))', // Account for safe area bottom
      height: 'calc(100vh - env(safe-area-inset-bottom, 0px))', // Account for safe area bottom
      // Override for landscape mode
      '@media (orientation: landscape)': {
        paddingTop: '20px !important',
        paddingBottom: '16px !important',
        marginTop: '0px !important',
        height: '100vh !important',
        maxHeight: '100vh !important',
        minHeight: '100vh !important',
        overflow: 'auto !important',
      },
    },
    // PWA standalone mode specific styles
    '@media (display-mode: standalone)': {
      paddingTop: 'calc(env(safe-area-inset-top, 0px) + 64px + 44px + 24px)', // Account for safe area in PWA
      paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px) + 60px)', // Extra bottom padding for PWA: 88px base + safe area + 60px buffer
      marginTop: '24px', // Add top margin to push content down and fix scroll position issue
      minHeight: 'calc(100vh - env(safe-area-inset-bottom, 0px))', // Account for safe area bottom in PWA
      height: 'calc(100vh - env(safe-area-inset-bottom, 0px))', // Account for safe area bottom in PWA
      // Override for landscape mode
      '@media (orientation: landscape)': {
        paddingTop: '20px !important',
        paddingBottom: '16px !important',
        marginTop: '0px !important',
        height: '100vh !important',
        maxHeight: '100vh !important',
        minHeight: '100vh !important',
        overflow: 'auto !important',
      },
    },
  },
}));
